// 권위:
//   - data/reports/UNIMPLEMENTED_INVENTORY.md §4 — 백테스트 결과 화면 신설 (화면 E).
//   - dashboard-api 측에서 endpoint 가 *방금 신설* (병렬 PR). 본 화면 진행 시점에 API 가
//     아직 없을 수 있음 → mock 데이터 fallback + 안내 배너 ("API 부재 — mock 데이터")
//
// REST 엔드포인트 (예정):
//   GET /api/v1/backtest/runs                 → BacktestRunSummary[]
//   GET /api/v1/backtest/runs/{id}            → BacktestRunDetail
//   GET /api/v1/backtest/runs/{id}/audit      → BacktestAuditPage
//
// fallback 정책:
//   - apiGet 시 ApiError.code === 'NOT_FOUND' / 'network_error' / httpStatus 404 →
//     mock 데이터 반환 + sourceTag='mock'. 그 외 에러는 throw.
//   - 다른 화면(eventsApi 등) 의 useQuery 패턴 정합 (staleTime 5분 — 이력 데이터).

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, ApiError } from '../../api/client';
import {
  MOCK_BACKTEST_RUNS,
  MOCK_BACKTEST_DETAILS,
  MOCK_BACKTEST_AUDIT,
  MOCK_BACKTEST_TRADES,
  type BacktestAuditPage,
  type BacktestEquityPoint,
  type BacktestRunDetail,
  type BacktestRunSummary,
  type BacktestSignalBreakdown,
  type BacktestTradesPage,
} from './tradingBacktestMock';

export type BacktestSource = 'api' | 'mock';

export interface BacktestRunsResult {
  rows: BacktestRunSummary[];
  source: BacktestSource;
}

export interface BacktestReportResult {
  detail: BacktestRunDetail;
  source: BacktestSource;
}

export interface BacktestAuditResult {
  page: BacktestAuditPage;
  source: BacktestSource;
}

export interface BacktestTradesResult {
  page: BacktestTradesPage;
  source: BacktestSource;
}

function isApiAbsent(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.httpStatus === 404) return true;
    if (err.code === 'network_error' || err.code === 'ERR_NETWORK') return true;
    // Spring Boot 가 매핑 안 된 경로에 대해 404 envelope 를 내릴 수도 있고,
    // 아예 reverse proxy 404 (envelope 없음) 일 수도 있음. 둘 다 mock.
    if (err.code === 'not_found' || err.code === 'NOT_FOUND') return true;
  }
  return false;
}

// ── /runs ─────────────────────────────────────────────────────

export async function fetchBacktestRuns(): Promise<BacktestRunsResult> {
  try {
    const data = await apiGet<{ runs: BacktestRunSummary[] }>('/api/v1/backtest/runs');
    return { rows: data.runs ?? [], source: 'api' };
  } catch (err) {
    if (isApiAbsent(err)) {
      return { rows: MOCK_BACKTEST_RUNS, source: 'mock' };
    }
    throw err;
  }
}

export function useBacktestRunsQuery(): UseQueryResult<BacktestRunsResult, Error> {
  return useQuery({
    queryKey: ['backtest', 'runs'],
    queryFn: fetchBacktestRuns,
    staleTime: 5 * 60_000,
  });
}

// ── /runs/{id} ─────────────────────────────────────────────────

// backend `/runs/{id}` 응답 schema: equityCurve 는 `[["2025-05-21","100000000.0"], ...]` 2-튜플 배열,
// signalBreakdown 필드는 응답에 없음 (Python backtest 러너 미산출). frontend interface 로 normalize.
type BackendEquityPoint = BacktestEquityPoint | [string, string | number];
type RawBacktestRunDetail = Omit<BacktestRunDetail, 'equityCurve' | 'signalBreakdown'> & {
  equityCurve?: BackendEquityPoint[];
  signalBreakdown?: BacktestSignalBreakdown[];
};

function normalizeRunDetail(raw: RawBacktestRunDetail): BacktestRunDetail {
  const equityCurve = (raw.equityCurve ?? []).map((p): BacktestEquityPoint => {
    if (Array.isArray(p)) return { date: String(p[0]), equity: Number(p[1]) };
    return p;
  });
  return {
    ...raw,
    equityCurve,
    signalBreakdown: raw.signalBreakdown ?? [],
  };
}

export async function fetchBacktestReport(id: string): Promise<BacktestReportResult> {
  try {
    const raw = await apiGet<RawBacktestRunDetail>(
      `/api/v1/backtest/runs/${encodeURIComponent(id)}`,
    );
    return { detail: normalizeRunDetail(raw), source: 'api' };
  } catch (err) {
    if (isApiAbsent(err)) {
      const detail = MOCK_BACKTEST_DETAILS[id] ?? MOCK_BACKTEST_DETAILS.__default__;
      return { detail: { ...detail, id }, source: 'mock' };
    }
    throw err;
  }
}

export function useBacktestReportQuery(
  id: string | undefined,
): UseQueryResult<BacktestReportResult, Error> {
  return useQuery({
    queryKey: ['backtest', 'run', id],
    queryFn: () => fetchBacktestReport(id as string),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

// ── /runs/{id}/audit ───────────────────────────────────────────

export interface BacktestAuditQuery {
  symbol?: string;
  eventCode?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

// backend AuditEvent: {timestamp, severity, sourceAgent, eventCode, summary, payload}.
// query params: page, size, event_code, symbol, since. frontend interface 로 normalize.
type RawAuditEvent = {
  timestamp?: string;
  occurredAt?: string;
  eventCode?: string;
  summary?: string;
  symbol?: string;
  payload?: Record<string, unknown>;
};
type RawAuditPage = {
  items?: RawAuditEvent[];
  total?: number;
  page?: number;
  size?: number;
  pageSize?: number;
};

function normalizeAuditPage(raw: RawAuditPage, fallback: { page: number; pageSize: number }): BacktestAuditPage {
  const items = (raw.items ?? []).map((row) => ({
    occurredAt: row.occurredAt ?? row.timestamp ?? '',
    symbol:
      row.symbol ??
      (typeof row.payload?.symbol === 'string' ? (row.payload.symbol as string) : ''),
    eventCode: row.eventCode ?? '',
    summary: row.summary ?? '',
  }));
  return {
    items,
    total: raw.total ?? items.length,
    page: raw.page ?? fallback.page,
    pageSize: raw.pageSize ?? raw.size ?? fallback.pageSize,
  };
}

export async function fetchBacktestAudit(
  id: string,
  query: BacktestAuditQuery = {},
): Promise<BacktestAuditResult> {
  try {
    // backend audit endpoint 의 query param 명칭: size / event_code / since.
    const params: Record<string, string | number> = {};
    if (query.symbol) params.symbol = query.symbol;
    if (query.eventCode) params.event_code = query.eventCode;
    if (query.from) params.since = query.from;
    if (query.page !== undefined) params.page = query.page;
    if (query.pageSize !== undefined) params.size = query.pageSize;
    const raw = await apiGet<RawAuditPage>(
      `/api/v1/backtest/runs/${encodeURIComponent(id)}/audit`,
      { params },
    );
    const page = normalizeAuditPage(raw, {
      page: query.page ?? 0,
      pageSize: query.pageSize ?? 50,
    });
    return { page, source: 'api' };
  } catch (err) {
    if (isApiAbsent(err)) {
      const all = MOCK_BACKTEST_AUDIT[id] ?? MOCK_BACKTEST_AUDIT.__default__;
      const filtered = all.filter((row) => {
        if (query.symbol && row.symbol !== query.symbol) return false;
        if (query.eventCode && !row.eventCode.startsWith(query.eventCode)) return false;
        if (query.from && row.occurredAt < query.from) return false;
        if (query.to && row.occurredAt > query.to) return false;
        return true;
      });
      const pageSize = query.pageSize ?? 50;
      const pageNum = query.page ?? 0;
      const start = pageNum * pageSize;
      return {
        page: {
          items: filtered.slice(start, start + pageSize),
          total: filtered.length,
          page: pageNum,
          pageSize,
        },
        source: 'mock',
      };
    }
    throw err;
  }
}

export function useBacktestAuditQuery(
  id: string | undefined,
  query: BacktestAuditQuery,
): UseQueryResult<BacktestAuditResult, Error> {
  return useQuery({
    queryKey: ['backtest', 'audit', id, query],
    queryFn: () => fetchBacktestAudit(id as string, query),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

// ── /runs/{id}/trades ──────────────────────────────────────────
// 권위: dashboard-api 측에서 병렬 신설 endpoint.
// 정렬/필터/페이지네이션은 phase 1 에서 *클라이언트 측*에서 수행한다 (rows 200~300건
// 수준 — 서버 정렬은 phase 2). API 응답이 오면 전체 trades 배열을 그대로 사용.

export interface BacktestTradesQuery {
  /** sort 옵션은 phase 2 서버 정렬을 위해 placeholder. 현재는 client 측에서 정렬 */
  sort?: 'net_pnl' | 'return_pct' | 'opened_at';
  direction?: 'asc' | 'desc';
  sector?: string;
  symbol?: string;
  limit?: number;
  offset?: number;
}

export async function fetchBacktestTrades(
  id: string,
  query: BacktestTradesQuery = {},
): Promise<BacktestTradesResult> {
  try {
    const params: Record<string, string | number> = {};
    if (query.sort) params.sort = query.sort;
    if (query.direction) params.direction = query.direction;
    if (query.sector) params.sector = query.sector;
    if (query.symbol) params.symbol = query.symbol;
    if (query.limit !== undefined) params.limit = query.limit;
    if (query.offset !== undefined) params.offset = query.offset;
    const page = await apiGet<BacktestTradesPage>(
      `/api/v1/backtest/runs/${encodeURIComponent(id)}/trades`,
      { params },
    );
    return { page, source: 'api' };
  } catch (err) {
    if (isApiAbsent(err)) {
      const mock = MOCK_BACKTEST_TRADES[id] ?? MOCK_BACKTEST_TRADES.__default__;
      return { page: { ...mock, runId: id }, source: 'mock' };
    }
    throw err;
  }
}

export function useBacktestTradesQuery(
  id: string | undefined,
  query: BacktestTradesQuery = {},
): UseQueryResult<BacktestTradesResult, Error> {
  return useQuery({
    queryKey: ['backtest', 'trades', id, query],
    queryFn: () => fetchBacktestTrades(id as string, query),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}
